/**
 * University API Service
 * Handles all course / lesson related API calls to the backend.
 *
 * NOTE: Endpoint paths are based on UNIVERSITY_COURSE_SYSTEM_IMPLEMENTATION_PLAN.md
 * and can be adjusted easily here if the backend contract changes.
 */

import { authenticatedFetch } from "./apiClient";
import { parseJsonOrThrow } from "../utils/apiRequestError";
import {
  Course,
  CoursesListResponse,
  EnrolledCoursesResponse,
  CourseDetailsResponse,
  LessonResponse,
  Enrollment,
  LessonProgress,
  CourseProgressSummary,
} from "../types/university";

export const universityApi = {
  /**
   * Get all published courses (no user-specific enrollment/progress).
   * GET /api/courses
   */
  async getCourses(): Promise<Course[]> {
    const response = await authenticatedFetch("/courses", {
      method: "GET",
    });
    const data = await parseJsonOrThrow<CoursesListResponse | Course[]>(response, "Failed to load courses");

    // Support both shapes: { courses: [...] } or direct array
    if (Array.isArray(data)) {
      return data;
    }
    return data.courses;
  },

  /**
   * Get course details, including enrollment + progress for current user.
   * GET /api/courses/{course_id}
   */
  async getCourse(courseId: string): Promise<CourseDetailsResponse> {
    const response = await authenticatedFetch(`/courses/${encodeURIComponent(courseId)}`, {
      method: "GET",
    });
    return parseJsonOrThrow<CourseDetailsResponse>(response, "Failed to load course");
  },

  /**
   * Enroll current user in a course.
   * POST /api/courses/{course_id}/enroll
   */
  async enrollInCourse(courseId: string): Promise<Enrollment> {
    const endpoint = `/courses/${encodeURIComponent(courseId)}/enroll`;
    const backendUrl = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:8000/api";
    const fullUrl = `${backendUrl}${endpoint}`;
    
    console.log(`[University API] Enrolling in course: ${courseId}`);
    console.log(`[University API] Full URL: POST ${fullUrl}`);
    console.log(`[University API] Expected backend endpoint: POST /api/courses/{courseId}/enroll`);
    
    try {
      const response = await authenticatedFetch(endpoint, {
        method: "POST",
      });
      console.log(`[University API] Response status: ${response.status}`);
      
      // Store the URL in the response object for error handling
      (response as any).requestUrl = fullUrl;
      
      return parseJsonOrThrow<Enrollment>(response, "Failed to enroll in course");
    } catch (error) {
      console.error(`[University API] Enrollment error for course ${courseId}:`, error);
      console.error(`[University API] Failed URL: POST ${fullUrl}`);
      
      // Enhance error with URL information
      if (error instanceof Error && !error.message.includes(fullUrl)) {
        (error as any).requestUrl = fullUrl;
        (error as any).courseId = courseId;
      }
      
      throw error;
    }
  },

  /**
   * Get all courses the current user is enrolled in, with progress.
   * GET /api/courses/enrolled
   */
  async getEnrolledCourses(): Promise<Course[]> {
    const response = await authenticatedFetch("/courses/enrolled", {
      method: "GET",
    });
    const data = await parseJsonOrThrow<EnrolledCoursesResponse | { courses: Course[] }>(
      response,
      "Failed to load enrolled courses"
    );
    return data.courses;
  },

  /**
   * Get a single lesson with progress & unlock info.
   *
   * Backend route (from plan):
   * GET /api/courses/{course_id}/modules/{module_slug}/lessons/{lesson_slug}
   *
   * NOTE: Our current frontend routes use numeric IDs; we can adapt once
   * the backend contract is finalized by adding an ID→slug mapping layer.
   */
  async getLesson(
    courseId: string,
    moduleSlug: string,
    lessonSlug: string,
  ): Promise<LessonResponse> {
    const response = await authenticatedFetch(
      `/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(
        moduleSlug,
      )}/lessons/${encodeURIComponent(lessonSlug)}`,
      {
        method: "GET",
      },
    );
    return parseJsonOrThrow<LessonResponse>(response, "Failed to load lesson");
  },

  /**
   * Mark a lesson as completed for the current user.
   *
   * POST /api/courses/{course_id}/modules/{module_slug}/lessons/{lesson_slug}/complete
   */
  async markLessonComplete(
    courseId: string,
    moduleSlug: string,
    lessonSlug: string,
    timeSpentSeconds: number = 0,
  ): Promise<LessonProgress> {
    const response = await authenticatedFetch(
      `/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(
        moduleSlug,
      )}/lessons/${encodeURIComponent(lessonSlug)}/complete`,
      {
        method: "POST",
        body: JSON.stringify({ time_spent_seconds: timeSpentSeconds }),
      },
    );
    return parseJsonOrThrow<LessonProgress>(response, "Failed to mark lesson complete");
  },

  /**
   * Mark a lesson as incomplete (uncomplete) for the current user.
   *
   * Uses PATCH /api/courses/{course_id}/modules/{module_slug}/lessons/{lesson_slug}/progress
   * with body: { is_completed: false }
   * 
   * Returns updated progress object with is_completed: false
   */
  async markLessonIncomplete(
    courseId: string,
    moduleSlug: string,
    lessonSlug: string,
  ): Promise<LessonProgress> {
    // Use the updateLessonProgress method with isCompleted: false
    return this.updateLessonProgress(courseId, moduleSlug, lessonSlug, {
      isCompleted: false,
    });
  },

  /**
   * Update lesson progress (time spent and/or completion status).
   *
   * PATCH /api/courses/{course_id}/modules/{module_slug}/lessons/{lesson_slug}/progress
   * 
   * Accepts JSON body with optional fields:
   * - time_spent_seconds: number (optional) - Time spent on lesson
   * - is_completed: boolean (optional) - Completion status (true = complete, false = incomplete)
   * 
   * Returns updated progress object with current completion status
   */
  async updateLessonProgress(
    courseId: string,
    moduleSlug: string,
    lessonSlug: string,
    updates: {
      timeSpentSeconds?: number;
      isCompleted?: boolean;
    },
  ): Promise<LessonProgress> {
    const body: { time_spent_seconds?: number; is_completed?: boolean } = {};
    
    if (updates.timeSpentSeconds !== undefined) {
      body.time_spent_seconds = updates.timeSpentSeconds;
    }
    
    if (updates.isCompleted !== undefined) {
      body.is_completed = updates.isCompleted;
    }
    
    const response = await authenticatedFetch(
      `/courses/${encodeURIComponent(courseId)}/modules/${encodeURIComponent(
        moduleSlug,
      )}/lessons/${encodeURIComponent(lessonSlug)}/progress`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    );
    
    return parseJsonOrThrow<LessonProgress>(response, "Failed to update lesson progress");
  },

  /**
   * Convenience: get progress summary for a specific course + user.
   *
   * Backend plan exposes this via course details, but we keep this
   * helper in case a dedicated endpoint is added later.
   */
  async getCourseProgress(
    courseId: string,
  ): Promise<CourseProgressSummary | null> {
    const course = await this.getCourse(courseId);
    return course.progress ?? null;
  },

  /**
   * Get lesson progress for a specific lesson.
   * This is used to check if a lesson is completed.
   * 
   * GET /api/courses/{course_id}/modules/{module_slug}/lessons/{lesson_slug}
   * Returns LessonResponse with progress.is_completed
   */
  async getLessonProgress(
    courseId: string,
    moduleSlug: string,
    lessonSlug: string,
  ): Promise<{ is_completed: boolean; last_accessed_at?: string | null } | null> {
    try {
      const lesson = await this.getLesson(courseId, moduleSlug, lessonSlug);
      return lesson.progress
        ? {
            is_completed: lesson.progress.is_completed,
            last_accessed_at: lesson.progress.last_accessed_at ?? null,
          }
        : null;
    } catch (error) {
      // If lesson doesn't exist or user doesn't have access, return null
      console.warn(`[University API] Could not fetch lesson progress for ${courseId}/${moduleSlug}/${lessonSlug}:`, error);
      return null;
    }
  },
};

